import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { Plus, Trash2, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function PurchaseOrders() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "user";
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [sites, setSites] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [show, setShow] = useState(false);
  const [delivery, setDelivery] = useState(null);
  // Compteur incrémental pour _uid stable des lignes (formulaire de commande + livraison)
  const lineUidRef = useRef(1);
  const nextUid = () => lineUidRef.current++;
  const [form, setForm] = useState({ supplier_id:"", site_id:"", note:"", lines:[{_uid: 0, product_id:"",quantity:1,unit_cost:0}] });

  const load = async () => {
    const [o, p, s, sup] = await Promise.all([api.get("/purchase-orders"), api.get("/products"), api.get("/sites"), api.get("/suppliers")]);
    setOrders(o.data); setProducts(p.data); setSites(s.data); setSuppliers(sup.data);
  };
  useEffect(()=>{load();},[]);

  const productMap = useMemo(()=>Object.fromEntries(products.map(p=>[p.id, p])), [products]);
  const siteMap = useMemo(()=>Object.fromEntries(sites.map(s=>[s.id, s])), [sites]);
  const supplierMap = useMemo(()=>Object.fromEntries(suppliers.map(s=>[s.id, s])), [suppliers]);

  const updateLine = (i, field, val) => {
    const lines = [...form.lines]; lines[i] = { ...lines[i], [field]: val }; setForm({ ...form, lines });
  };
  const addLine = () => setForm({ ...form, lines: [...form.lines, { _uid: nextUid(), product_id:"", quantity:1, unit_cost:0 }] });
  const rmLine = (i) => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) });

  const submit = async e => {
    e.preventDefault();
    try {
      const payload = { ...form, lines: form.lines.map(l => ({ ...l, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost) })) };
      await api.post("/purchase-orders", payload);
      toast.success("Commande créée");
      setShow(false); setForm({ supplier_id:"", site_id:"", note:"", lines:[{_uid: nextUid(), product_id:"",quantity:1,unit_cost:0}] });
      load();
    } catch(e){ toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDelivery = (po) => {
    const recvMap = Object.fromEntries((po.received_lines || []).map(r => [r.product_id, r.quantity]));
    setDelivery({
      po,
      lines: po.lines.map(l => ({
        product_id: l.product_id,
        ordered: l.quantity,
        already: recvMap[l.product_id] || 0,
        quantity: 0,
        batch_number: "",
        expiry_date: "",
      }))
    });
  };

  const submitDelivery = async () => {
    try {
      const received = delivery.lines.filter(l => Number(l.quantity) > 0).map(l => ({
        product_id: l.product_id, quantity: Number(l.quantity), batch_number: l.batch_number, expiry_date: l.expiry_date,
      }));
      if (received.length === 0) return toast.error("Aucune quantité saisie");
      await api.post("/purchase-orders/deliver", { po_id: delivery.po.id, received });
      toast.success("Livraison enregistrée");
      setDelivery(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Commandes d'achat" subtitle="Approvisionnement · Livraisons partielles"
        actions={canEdit && <button data-testid="new-po-btn" className="btn-primary flex items-center gap-2" onClick={()=>setShow(!show)}><Plus size={14}/>Nouvelle commande</button>}
      />

      {show && canEdit && (
        <Surface title="Nouvelle commande">
          <form onSubmit={submit} className="p-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div><label className="label-mono block mb-1.5">Fournisseur</label>
                <select data-testid="po-supplier" required className="input-base" value={form.supplier_id} onChange={e=>setForm({...form,supplier_id:e.target.value})}>
                  <option value="">— Choisir —</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div><label className="label-mono block mb-1.5">Site de livraison</label>
                <select data-testid="po-site" required className="input-base" value={form.site_id} onChange={e=>setForm({...form,site_id:e.target.value})}>
                  <option value="">— Choisir —</option>{sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div><label className="label-mono block mb-1.5">Note</label><input className="input-base" value={form.note} onChange={e=>setForm({...form,note:e.target.value})}/></div>
            </div>
            <div className="space-y-2">
              <div className="label-mono">Lignes de commande</div>
              {form.lines.map((l, i) => (
                <div key={l._uid ?? `legacy-${i}`} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6"><select required className="input-base" value={l.product_id} onChange={e=>updateLine(i,"product_id",e.target.value)}>
                    <option value="">— Produit —</option>{products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select></div>
                  <div className="col-span-2"><input type="number" min="1" required className="input-base" placeholder="Qté" value={l.quantity} onChange={e=>updateLine(i,"quantity",e.target.value)}/></div>
                  <div className="col-span-3"><input type="number" step="0.01" required className="input-base" placeholder="Coût unit. (F)" value={l.unit_cost} onChange={e=>updateLine(i,"unit_cost",e.target.value)}/></div>
                  <div className="col-span-1"><button type="button" onClick={()=>rmLine(i)} className="text-[var(--text-muted)] hover:text-[var(--neon-pink)]"><Trash2 size={14}/></button></div>
                </div>
              ))}
              <button type="button" onClick={addLine} className="btn-ghost text-xs">+ Ajouter une ligne</button>
            </div>
            <div className="flex gap-2"><button data-testid="submit-po" type="submit" className="btn-primary">Créer la commande</button><button type="button" className="btn-ghost" onClick={()=>setShow(false)}>Annuler</button></div>
          </form>
        </Surface>
      )}

      {delivery && (
        <Surface title={`Livraison — Commande ${delivery.po.id.slice(0,8)}`}>
          <div className="p-5 space-y-3">
            {delivery.lines.map((l, i) => {
              const remaining = l.ordered - l.already;
              return (
                <div key={l.product_id || `dline-${i}`} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3"><div className="label-mono">Produit</div><div className="text-sm">{productMap[l.product_id]?.name}</div></div>
                  <div className="col-span-2"><div className="label-mono">Restant</div><div className="font-data neon-text-cyan">{remaining} / {l.ordered}</div></div>
                  <div className="col-span-2"><label className="label-mono block mb-1">Reçu</label>
                    <input type="number" min="0" max={remaining} className="input-base" value={l.quantity}
                      onChange={e=>setDelivery({...delivery, lines: delivery.lines.map((x,j)=>j===i?{...x,quantity:e.target.value}:x)})}/></div>
                  <div className="col-span-2"><label className="label-mono block mb-1">N° Lot</label>
                    <input className="input-base" value={l.batch_number}
                      onChange={e=>setDelivery({...delivery, lines: delivery.lines.map((x,j)=>j===i?{...x,batch_number:e.target.value}:x)})}/></div>
                  <div className="col-span-3"><label className="label-mono block mb-1">Péremption</label>
                    <input type="date" className="input-base" value={l.expiry_date}
                      onChange={e=>setDelivery({...delivery, lines: delivery.lines.map((x,j)=>j===i?{...x,expiry_date:e.target.value}:x)})}/></div>
                </div>
              );
            })}
            <div className="flex gap-2 pt-2"><button data-testid="submit-delivery" onClick={submitDelivery} className="btn-primary flex items-center gap-2"><PackageCheck size={14}/>Valider la livraison</button>
              <button className="btn-ghost" onClick={()=>setDelivery(null)}>Annuler</button></div>
          </div>
        </Surface>
      )}

      <Surface title="Commandes">
        {orders.length === 0 ? <Empty/> : (
          <table className="data-table">
            <thead><tr><th>Date</th><th>Fournisseur</th><th>Site</th><th>Articles</th><th>Total</th><th>Statut</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id}>
                  <td className="font-data text-[var(--text-muted)] text-xs">{o.created_at?.slice(0,10)}</td>
                  <td>{supplierMap[o.supplier_id]?.name}</td>
                  <td className="text-[var(--text-muted)]">{siteMap[o.site_id]?.name}</td>
                  <td className="font-data">{o.lines.length}</td>
                  <td className="font-data">{o.total.toLocaleString("fr-FR")} F</td>
                  <td>{o.status === "complet" ? <span className="badge-success">COMPLET</span> : o.status === "partiel" ? <span className="badge-warn">PARTIEL</span> : <span className="badge-info">EN ATTENTE</span>}</td>
                  {canEdit && <td>{o.status !== "complet" && <button data-testid={`deliver-${o.id}`} onClick={()=>openDelivery(o)} className="btn-ghost text-xs">Livrer</button>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Surface>
    </div>
  );
}
